import { ArrowRight, Clock, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ExtensionHistoryPanel } from './ExtensionHistoryPanel';
import { StatusBadge } from './StatusBadge';
import { useStatusHistory } from '@/hooks/use-loans';
import { fmtDate, fmtDateOnly } from '@/lib/format';
import { LOAN_STATUS_CONFIG, type Loan, type LoanStatus } from '@/types';

const LOAN_TYPE_LABELS: Record<string, string> = {
  PERSONAL: 'Personal',
  BUSINESS: 'Business',
  MORTGAGE: 'Mortgage',
  AUTO: 'Auto',
  LINE_OF_CREDIT: 'Line of Credit',
};

function fmt(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(num);
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-500 mb-0.5">{label}</dt>
      <dd className="text-sm font-medium text-gray-900">{value}</dd>
    </div>
  );
}

interface Props {
  loan: Loan;
}

export function LoanOverviewTab({ loan }: Props) {
  return (
    <div className="space-y-6">
      {/* Loan details */}
      <section className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Loan Details</h3>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
          <Field label="Loan Number" value={<span className="font-mono">{loan.loanNumber}</span>} />
          <Field label="Type" value={LOAN_TYPE_LABELS[loan.loanType] ?? loan.loanType} />
          <Field label="Status" value={<StatusBadge status={loan.status} />} />
          <Field label="Principal" value={fmt(loan.principal)} />
          {loan.interestMethod === 'FLAT_AMOUNT' ? (
            <Field
              label="Interest"
              value={
                <span>
                  {fmt(loan.flatInterestAmount ?? '0')}{' '}
                  <span className="text-[10px] text-gray-400">(flat)</span>
                </span>
              }
            />
          ) : (
            <Field label="Interest Rate" value={`${parseFloat(loan.interestRate).toFixed(2)}%`} />
          )}
          {loan.defaultRate && (
            <Field label="Default Rate" value={`${parseFloat(loan.defaultRate).toFixed(2)}%`} />
          )}
          <Field label="Term" value={`${loan.termMonths} months`} />
          <Field label="Application Date" value={fmtDateOnly(loan.applicationDate)} />
          <Field label="Settlement Date" value={fmtDateOnly(loan.settlementDate)} />
          <Field label="Approval Date" value={fmtDate(loan.approvalDate)} />
          <Field label="Disbursement Date" value={fmtDateOnly(loan.disbursementDate)} />
          <Field label="Maturity Date" value={fmtDateOnly(loan.maturityDate)} />
          {loan.defaultDate && <Field label="Default Date" value={fmtDate(loan.defaultDate)} />}
          <Field label="Closed Date" value={fmtDate(loan.closedDate)} />
        </dl>
      </section>

      {/* Loan References */}
      {(loan.internalReference || loan.lawFirmReference) && (
        <section className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">References</h3>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
            {loan.internalReference && (
              <Field
                label="Internal Reference"
                value={<span className="font-mono">{loan.internalReference}</span>}
              />
            )}
            {loan.lawFirmReference && (
              <Field
                label="Law Firm Reference"
                value={<span className="font-mono">{loan.lawFirmReference}</span>}
              />
            )}
          </dl>
        </section>
      )}

      {/* Establishment Costs & Fees */}
      {(loan.establishmentFee || loan.brokerageFee || loan.legalCosts || loan.otherCosts) && (
        <section className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Establishment Costs & Fees</h3>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
            {loan.establishmentFee && (
              <Field label="Establishment Fee" value={fmt(loan.establishmentFee)} />
            )}
            {loan.brokerageFee && <Field label="Brokerage Fee" value={fmt(loan.brokerageFee)} />}
            {loan.legalCosts && (
              <Field
                label="Legal Costs"
                value={
                  <span>
                    {fmt(loan.legalCosts)}
                    {loan.legalCostsRecovered && (
                      <span className="ml-1.5 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                        Recovered
                      </span>
                    )}
                  </span>
                }
              />
            )}
            {loan.otherCosts && (
              <Field
                label={
                  loan.otherCostsDescription
                    ? `Other: ${loan.otherCostsDescription}`
                    : 'Other Costs'
                }
                value={fmt(loan.otherCosts)}
              />
            )}
          </dl>
        </section>
      )}

      {/* Fee split / lender retained */}
      {(loan.establishmentFeeRetained || loan.brokerageFeeRetained || loan.referrerCommission) && (
        <section className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Lender Fee Split</h3>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
            {loan.establishmentFeeRetained && (
              <Field
                label="Establishment Retained"
                value={
                  <span className="text-indigo-700">{fmt(loan.establishmentFeeRetained)}</span>
                }
              />
            )}
            {loan.brokerageFeeRetained && (
              <Field
                label="Brokerage Retained"
                value={<span className="text-indigo-700">{fmt(loan.brokerageFeeRetained)}</span>}
              />
            )}
            {loan.referrerCommission && (
              <Field
                label="Referrer Commission"
                value={<span className="text-red-600">{fmt(loan.referrerCommission)}</span>}
              />
            )}
          </dl>
        </section>
      )}

      {/* Referrer */}
      {loan.referrer && (
        <section className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Referrer</h3>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
            <Field label="Name" value={loan.referrer.name} />
            {loan.referrer.company && <Field label="Company" value={loan.referrer.company} />}
            {loan.referrer.commissionRate && (
              <Field
                label="Commission Rate"
                value={`${parseFloat(loan.referrer.commissionRate).toFixed(2)}%`}
              />
            )}
          </dl>
        </section>
      )}

      {/* Borrower info */}
      {loan.borrower && (
        <section className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Borrower</h3>
            <Link
              to={`/borrowers/${loan.borrower.id}`}
              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
            >
              View Profile
            </Link>
          </div>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
            <Field label="Name" value={loan.borrower.name} />
            <Field label="Email" value={loan.borrower.email ?? '—'} />
            <Field label="Phone" value={loan.borrower.phone ?? '—'} />
          </dl>
        </section>
      )}

      {/* Ledger balances */}
      {loan.ledger?.balances && loan.ledger.balances.length > 0 && (
        <section className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Account Balances</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {loan.ledger.balances.map(b => (
              <div key={b.id} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 capitalize mb-1">{b.indicator}</p>
                <p className="text-lg font-semibold text-gray-900">{fmt(b.balance)}</p>
                <div className="flex gap-3 mt-1 text-[11px] text-gray-400">
                  <span>Cr: {fmt(b.creditBalance)}</span>
                  <span>Dr: {fmt(b.debitBalance)}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Assignments */}
      {loan.assignments && loan.assignments.length > 0 && (
        <section className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Team</h3>
          <div className="flex flex-wrap gap-3">
            {loan.assignments.map(a => (
              <div key={a.id} className="flex items-center gap-2.5 bg-gray-50 rounded-lg px-3 py-2">
                <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
                  <span className="text-xs font-semibold">
                    {a.user ? `${a.user.firstName[0]}${a.user.lastName[0]}` : '?'}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-900">
                    {a.user ? `${a.user.firstName} ${a.user.lastName}` : 'Unknown'}
                  </p>
                  <p className="text-[11px] text-gray-400 capitalize">{a.role.toLowerCase()}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Extension History */}
      {loan.extensions && loan.extensions.length > 0 && (
        <section className="bg-white rounded-lg border border-gray-200 p-5">
          <ExtensionHistoryPanel extensions={loan.extensions} />
        </section>
      )}

      {/* Status Timeline */}
      <StatusTimeline loanId={loan.id} />
    </div>
  );
}

function StatusTimeline({ loanId }: { loanId: string }) {
  const { data: history, isLoading } = useStatusHistory(loanId);

  if (isLoading || !history || history.length === 0) return null;

  return (
    <section className="bg-white rounded-lg border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Status History</h3>
      <div className="relative space-y-0">
        {history.map((t, i) => {
          const isLast = i === history.length - 1;
          const fromCfg = LOAN_STATUS_CONFIG[t.fromStatus as LoanStatus];
          const toCfg = LOAN_STATUS_CONFIG[t.toStatus as LoanStatus];
          const date = new Date(t.createdAt);
          const timeStr = date.toLocaleDateString('en-AU', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
          const timeOfDay = date.toLocaleTimeString('en-AU', {
            hour: '2-digit',
            minute: '2-digit',
          });
          const userName = t.user ? `${t.user.firstName} ${t.user.lastName}` : 'System';

          return (
            <div key={t.id} className="flex gap-3">
              {/* Timeline line + dot */}
              <div className="flex flex-col items-center">
                <div
                  className="w-3 h-3 rounded-full border-2 shrink-0 mt-1"
                  style={{
                    borderColor: toCfg?.color ?? '#6b7280',
                    backgroundColor: isLast ? (toCfg?.color ?? '#6b7280') : 'white',
                  }}
                />
                {!isLast && <div className="w-px flex-1 bg-gray-200 my-1" />}
              </div>

              {/* Content */}
              <div className={`pb-4 ${isLast ? '' : ''}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                    style={{
                      color: fromCfg?.color ?? '#6b7280',
                      backgroundColor: `${fromCfg?.color ?? '#6b7280'}15`,
                    }}
                  >
                    {fromCfg?.label ?? t.fromStatus}
                  </span>
                  <ArrowRight className="w-3 h-3 text-gray-300" />
                  <span
                    className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                    style={{
                      color: toCfg?.color ?? '#6b7280',
                      backgroundColor: `${toCfg?.color ?? '#6b7280'}15`,
                    }}
                  >
                    {toCfg?.label ?? t.toStatus}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-gray-400">
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {userName}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {timeStr} at {timeOfDay}
                  </span>
                </div>
                {t.reason && <p className="text-xs text-gray-500 mt-1 italic">"{t.reason}"</p>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
